import React from 'react';
import './App.css';
import { Background, KnowTheSongs, Song } from './Components';
import { ThemeProvider } from 'styled-components';
import { Countdown } from './Pages/Home';
import { Avatar, Box, IconButton, Link, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';


// 1) MUI X Date Calendar + date-fns for Hebrew usage

import { isSameDay, parseISO } from 'date-fns';
import { CustomCalendar } from './Components/CustomCalandar';
import { CalendarIcon } from '@mui/x-date-pickers';
import CountUpTimer from './Components/CountUpTimer';


const shows = [
  { key: 1, location: 'ארנה ירושלים', date: '2025-02-19T20:45:00+02:00' },
  { key: 2, location: 'היכל תוצרת הארץ אקספו תל אביב', date: '2025-02-23T20:45:00+02:00' },
  { key: 3, location: 'היכל תוצרת הארץ אקספו תל אביב', date: '2025-02-24T20:45:00+02:00' },
  { key: 4, location: 'היכל תוצרת הארץ אקספו תל אביב', date: '2025-02-26T20:45:00+02:00' },
  { key: 5, location: 'היכל תוצרת הארץ אקספו תל אביב', date: '2025-02-27T20:45:00+02:00' },
  { key: 6, location: 'היכל תוצרת הארץ אקספו תל אביב', date: '2025-03-01T21:00:00+02:00' },
  { key: 7, location: 'היכל תוצרת הארץ אקספו תל אביב', date: '2025-03-03T20:45:00+02:00' },
  { key: 8, location: 'היכל תוצרת הארץ אקספו תל אביב', date: '2025-03-04T20:45:00+02:00' },
  { key: 9, location: 'היכל תוצרת הארץ אקספו תל אביב', date: '2025-03-06T20:45:00+02:00' }
];

function App() {
  // make rtl theme for mui
  const theme = createTheme({
    direction: 'rtl',
    typography: {
      fontFamily: `"Rubik", "Helvetica", "Arial", sans-serif`
    },
    palette: {
      primary: {
        light: '#e8b2bd',
        main: '#d4929d',
        dark: '#bf717a',
        contrastText: '#fff',
      },
      secondary: {
        light: '#ff7961',
        main: '#f44336',
        dark: '#ba000d',
        contrastText: '#000',
      },
    }
  });

  // Maintain which show is currently selected
  const [currentShow, setCurrentShow] = React.useState(0);
  const isMobile = useMediaQuery("(max-width: 700px)");

  // Maintain which date is selected in the calendar
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(
    new Date(shows[currentShow].date)
  );

  const [songs, setSongs] = React.useState<Song[]>([]);
  const [openCalendar, setOpenCalendar] = React.useState(false);

  // Hebrew names of weekdays
  const daysOfWeek = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

  // Countdown logic
  let endsAt = new Date(shows[currentShow].date).getTime();

  // Convert the show dates to real Date objects for easy comparison
  // const showDates = shows.map((s) => new Date(s.date));

  // Only allow selecting a date if it is one of our show dates


  function handleDateSelect(date: Date | null) {
    if (date) {
      setSelectedDate(date);
      const foundIndex = shows.findIndex((show) => {
        const showDate = parseISO(show.date);
        return isSameDay(showDate, date);
      });
      setCurrentShow(foundIndex);
    }
  }

  function toggleCalendar() {
    setOpenCalendar(!openCalendar);
  }

  return (
    <div className="App">
      <ThemeProvider
        theme={{
          colors: { primary: '#ff0000', secondary: '#00ff00', tertiary: '#0000ff' },
          fonts: { primary: 'Rubik' },
          dir: 'rtl',
        }}
      >
        <MuiThemeProvider theme={theme}>
          {/* Background behind everything */}
          <Background songs={songs} />

          {/* 
             Flex container that holds:
             1. The pink box (centered as before)
             2. The Avatar to the right of it
          */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              // space from the top
              marginTop: '3rem',
              transform: isMobile ? 'scale(0.74)' : 'scale(1)',
              transformOrigin: 'top center',
              // gap between the pink box and avatar
              gap: 2,
              // width: 'fit-content'
            }}
          >
            <Box sx={{ width: 80, height: 80 }}></Box>
            {/* The pink box */}
            <Box
              sx={{
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '0px',
                  // left: '50%',
                  left: '100%',
                  transform: 'translateX(-50%), rotate (180deg)',
                  width: 0,
                  height: 0,
                  borderRight: '10px solid transparent',
                  borderBottom: '18px solid transparent',
                  borderLeft: '20px solid #ffecec',
                  // boxShadow: '0px 0px 10px rgba(236, 163, 163, 0.3)',

                  backdropFilter: 'blur(1.5px) hue-rotate(180deg)',

                },
                backdropFilter: 'blur(1.5px) hue-rotate(180deg)',
                background: 'rgba(236, 163, 163, 0.2)',
                boxShadow: '0px 0px 10px rgba(236, 163, 163, 0.3)',
                borderRadius: '10px 0 10px 10px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'height 0.5s ease-in-out',
                minWidth: 'calc(60vw + 100px)'
                // width: 'fit-content'
              }}
            >
              {/* time until Idan Amedi's concert - in Hebrew */}
              <Box sx={{ direction: 'rtl', display: 'flex', flexDirection: 'row' }} mt={2} mb={2}>
                <Box>
                  <Typography variant="h4" sx={{ display: 'flex', justifyContent: 'center' }}>
                    "גם אשוב להלחם בעד המדינה שלי"
                  </Typography>
                  <Typography variant="h6" component="span" sx={{ margin: 'auto', marginRight: '5px' }}>
                    - עידן עמדי
                  </Typography>
                </Box>
              </Box>

              {/* todo !! the seconds shouldnt change even if the time is changed. use a ref or a useState to state the initialization */}
              <Countdown endsAt={endsAt} />

              <Typography variant="h5" sx={{ marginTop: 2, direction: 'rtl' }}>
                עד להופעת עידן עמדי ב:
              </Typography>
              {/* todo: if line break needed, divide the words into two even lines */}
              {/* todo: the animation of the numbers when date changes */}
              <Typography variant="h4">{shows[currentShow].location}</Typography>

              <Box display="flex"
                flexDirection="row"
                justifyContent="center"
                alignItems="center"
              >
                <Box sx={{ width: 48, height: 48 }}></Box>


                <Typography variant="h6" sx={{ direction: 'rtl', whiteSpace: 'nowrap' }}>
                  יום {daysOfWeek[new Date(shows[currentShow].date).getDay()]}
                  {' | '}
                  {selectedDate && selectedDate.toLocaleDateString('he-IL')}
                  {' | '}
                  {new Date(shows[currentShow].date).toLocaleTimeString('he-IL', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
                <Tooltip title="לכל ההופעות" placement="bottom">
                  <IconButton
                    color="primary"
                    sx={{
                      '@keyframes bg': {
                        '0%': {
                          backgroundColor: `rgba(${theme.palette.primary.main
                            .replace('#', '')
                            .match(/.{1,2}/g)
                            ?.map((hex) => parseInt(hex, 16))
                            .join(', ')}, 0.16)`,
                        },
                        '100%': {
                          backgroundColor: `rgba(${theme.palette.primary.light
                            .replace('#', '')
                            .match(/.{1,2}/g)
                            ?.map((hex) => parseInt(hex, 16))
                            .join(', ')}, 0.1)`,
                        },
                      },
                      animation: '1s ease-in-out bg alternate infinite',
                      marginLeft: 1,
                    }}
                    onClick={toggleCalendar}
                    aria-description="בחר תאריך"
                    size="medium"
                  >
                    <CalendarIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box
                sx={{
                  height: openCalendar ? 'calc(2.2rem * 5.6 + 30px)' : 0,
                  overflow: 'hidden',
                  transition: 'height 0.5s ease-in-out',
                }}
              >
                {openCalendar && (
                  <CustomCalendar
                    shows={shows}
                    selectedDate={selectedDate}
                    onDateSelect={handleDateSelect}
                    minDate={new Date(2025, 1, 1)}
                    maxDate={new Date(2025, 2, 31)}
                    hideNonClickableRows
                    hideCalandar={() => setOpenCalendar(false)}
                  />
                )}
              </Box>

              <Box>
                <KnowTheSongs songs={songs} setSongs={setSongs} />
              </Box>
            </Box>

            {/* The Avatar to the right of the pink box */}
            <Avatar
              component="span"
              alt="Idan Amedi"
              src="/image.png"
              sx={{
                width: 80,
                height: 80,
                border: '1px solid rgba(0, 0, 0, 0.1)',
                // no absolute positioning
              }}
            />
          </Box>
        </MuiThemeProvider>
      </ThemeProvider>


      <Box sx={{
        position: 'fixed',
        bottom: 5,
        left: 10
      }}>Built by <Link href="https://www.linkedin.com/in/ayelet-w">Ayelet Wasserman</Link></Box>
      <Box sx={{
        position: 'fixed',
        bottom: isMobile ? 23 : 5,
        right: 10
      }}>כל הזכויות שמורות לעידן עמדי, מאקו ומרווה שרון</Box>

      <CountUpTimer />
    </div>
  );
}

export default App;
